import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { WebThemeProvider } from "@/context/WebThemeContext";
import SeoHelmet from "@/components/web/SeoHelmet";
import NavV2 from "@/components/web/home/NavV2";
import FooterV2 from "@/components/web/home/FooterV2";
import Hero from "@/components/web/home/Hero";
import FrameworkStrip from "@/components/web/home/FrameworkStrip";
import CapabilityCards from "@/components/web/home/CapabilityCards";
import AskDoWatchRemember from "@/components/web/home/AskDoWatchRemember";
import MultiModelSection from "@/components/web/home/MultiModelSection";
import AskComposerSection from "@/components/web/home/AskComposerSection";
import HumanAiSection from "@/components/web/home/HumanAiSection";
import ActionBarSection from "@/components/web/home/ActionBarSection";
import AutomationSection from "@/components/web/home/AutomationSection";
import MeetingIntelligenceSection from "@/components/web/home/MeetingIntelligenceSection";
import MemorySection from "@/components/web/home/MemorySection";
import AudienceTabs from "@/components/web/home/AudienceTabs";
import BusinessIntelligenceSection from "@/components/web/home/BusinessIntelligenceSection";
import AppsSection from "@/components/web/home/AppsSection";
import HowItWorksSection from "@/components/web/home/HowItWorksSection";
import DifferentiationSection from "@/components/web/home/DifferentiationSection";
import PlansSection from "@/components/web/home/PlansSection";
import FinalCTASection from "@/components/web/home/FinalCTASection";

/* ============================================================================
   TeamNest.ai — Public homepage (route: "/" and "/v2").
   Positioned as an AI Collaboration Workspace built on one loop:
   Ask · Collaborate · Do · Automate · Remember. Enterprise knowledge-continuity
   is retained as the premium Business story, not the whole homepage.
   Old enterprise-only homepage preserved at /legacy.
   ========================================================================== */

/** Smoothly scroll to a hash target when arriving via /#section links. */
function useHashScroll() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() =>
        el.scrollIntoView({ behavior: "smooth", block: "start" })
      );
    }
  }, [hash]);
}

export default function WebHomeV2() {
  useHashScroll();
  return (
    <WebThemeProvider forceDark>
      <SeoHelmet
        title="TeamNest.ai — AI Collaboration Workspace for People and Teams"
        description="Research with multiple AI models, collaborate with people, turn answers into actions, automate repetitive work, and build lasting knowledge in one connected workspace."
        path="/"
      />
      <div className="min-h-screen bg-[var(--w-bg)] text-[var(--w-text)]">
        <NavV2 />
        <main className="pt-16">
          <Hero />
          <FrameworkStrip />
          <CapabilityCards />
          <AskDoWatchRemember />
          <MultiModelSection />
          <AskComposerSection />
          <HumanAiSection />
          <ActionBarSection />
          <AutomationSection />
          <MeetingIntelligenceSection />
          <MemorySection />
          <AudienceTabs />
          <BusinessIntelligenceSection />
          <AppsSection />
          <HowItWorksSection />
          <DifferentiationSection />
          <PlansSection />
          <FinalCTASection />
        </main>
        <FooterV2 />
      </div>
    </WebThemeProvider>
  );
}
